import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import {
  fetchCourseById,
  createCourse,
  updateCourse,
} from "./adminCourseService";
import "./adminCourses.css";

const LEVELS = [
  { value: "BEGINNER", label: "Beginner" },
  { value: "INTERMEDIATE", label: "Intermediate" },
  { value: "PROFESSIONAL", label: "Professional" },
  { value: "SPEAK_COMPLETE", label: "Speak Complete" },
];

const slugify = (str) =>
  str
    .toLowerCase()
    .trim()
    .replace(/[^a-z0-9\s-]/g, "")
    .replace(/\s+/g, "-");

const EMPTY = {
  name: "",
  slug: "",
  level: "BEGINNER",
  shortDescription: "",
  description: "",
  learningObjectives: "",
  totalLessons: 21,
  totalDays: 21,
  sessionDurationMins: 30,
  batchModeEnabled: true,
  priceBatchRupees: "",
  personalModeEnabled: false,
  pricePersonalRupees: "",
  currency: "INR",
  minBatchSize: 4,
  maxBatchSize: 6,
  thumbnailUrl: "",
};

const AdminCourseFormPage = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const isEdit = Boolean(id);

  const [form, setForm] = useState(EMPTY);
  const [loading, setLoading] = useState(isEdit);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    if (!isEdit) return;
    fetchCourseById(id)
      .then((res) => {
        const c = res.data;
        setForm({
          name: c.name || "",
          slug: c.slug || "",
          level: c.level || "BEGINNER",
          shortDescription: c.shortDescription || "",
          description: c.description || "",
          learningObjectives: c.learningObjectives || "",
          totalLessons: c.totalLessons ?? 21,
          totalDays: c.totalDays ?? 21,
          sessionDurationMins: c.sessionDurationMins ?? 30,
          batchModeEnabled: !!c.batchModeEnabled,
          priceBatchRupees: c.priceBatch != null ? c.priceBatch / 100 : "",
          personalModeEnabled: !!c.personalModeEnabled,
          pricePersonalRupees: c.pricePersonal != null ? c.pricePersonal / 100 : "",
          currency: c.currency || "INR",
          minBatchSize: c.minBatchSize ?? 4,
          maxBatchSize: c.maxBatchSize ?? 6,
          thumbnailUrl: c.thumbnailUrl || "",
        });
      })
      .catch(() => setError("Failed to load course."))
      .finally(() => setLoading(false));
  }, [id, isEdit]);

  const set = (field, value) =>
    setForm((prev) => {
      const next = { ...prev, [field]: value };
      // auto-generate slug only when creating and name changes
      if (field === "name" && !isEdit) next.slug = slugify(value);
      return next;
    });

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");

    if (form.batchModeEnabled && !form.priceBatchRupees) {
      setError("Enter batch price or disable batch mode.");
      return;
    }
    if (form.personalModeEnabled && !form.pricePersonalRupees) {
      setError("Enter personal price or disable personal mode.");
      return;
    }

    const payload = {
      name: form.name,
      slug: form.slug,
      level: form.level,
      shortDescription: form.shortDescription,
      description: form.description,
      learningObjectives: form.learningObjectives,
      totalLessons: Number(form.totalLessons),
      totalDays: Number(form.totalDays),
      sessionDurationMins: Number(form.sessionDurationMins),
      batchModeEnabled: form.batchModeEnabled,
      priceBatch: form.batchModeEnabled
        ? Math.round(Number(form.priceBatchRupees) * 100)
        : null,
      personalModeEnabled: form.personalModeEnabled,
      pricePersonal: form.personalModeEnabled
        ? Math.round(Number(form.pricePersonalRupees) * 100)
        : null,
      currency: form.currency,
      minBatchSize: Number(form.minBatchSize),
      maxBatchSize: Number(form.maxBatchSize),
      thumbnailUrl: form.thumbnailUrl,
    };

    setSaving(true);
    try {
      if (isEdit) {
        await updateCourse(id, payload);
      } else {
        await createCourse(payload);
      }
      navigate("/admin/courses");
    } catch (err) {
      const msg =
        err?.response?.data?.message ||
        err?.response?.data ||
        "Save failed. Please try again.";
      setError(String(msg));
    } finally {
      setSaving(false);
    }
  };

  if (loading) return <div className="ac-loading">Loading…</div>;

  return (
    <div>
      <div className="ac-header">
        <h2>{isEdit ? "Edit Course" : "New Course"}</h2>
        <button className="ac-btn ac-btn-ghost" onClick={() => navigate("/admin/courses")}>
          ← Back
        </button>
      </div>

      <form className="ac-form-card" onSubmit={handleSubmit}>
        {error && (
          <div
            style={{
              background: "#fee2e2",
              color: "#b91c1c",
              padding: "0.6rem 0.9rem",
              borderRadius: "6px",
              marginBottom: "1rem",
              fontSize: "0.88rem",
            }}
          >
            {error}
          </div>
        )}

        {/* Name + Slug */}
        <div className="ac-form-row">
          <div className="ac-form-group">
            <label>Course Name *</label>
            <input
              required
              value={form.name}
              onChange={(e) => set("name", e.target.value)}
              placeholder="e.g. Beginner English"
            />
          </div>
          <div className="ac-form-group">
            <label>Slug *</label>
            <input
              required
              value={form.slug}
              onChange={(e) => set("slug", slugify(e.target.value))}
              placeholder="beginner-english"
            />
          </div>
        </div>

        {/* Level + Currency */}
        <div className="ac-form-row">
          <div className="ac-form-group">
            <label>Level *</label>
            <select value={form.level} onChange={(e) => set("level", e.target.value)}>
              {LEVELS.map((l) => (
                <option key={l.value} value={l.value}>{l.label}</option>
              ))}
            </select>
          </div>
          <div className="ac-form-group">
            <label>Currency</label>
            <input value={form.currency} onChange={(e) => set("currency", e.target.value)} />
          </div>
        </div>

        {/* Short description */}
        <div className="ac-form-group">
          <label>Short Description *</label>
          <input
            required
            value={form.shortDescription}
            onChange={(e) => set("shortDescription", e.target.value)}
            placeholder="One-line summary shown on course cards"
          />
        </div>

        {/* Full description */}
        <div className="ac-form-group">
          <label>Full Description</label>
          <textarea
            rows={4}
            value={form.description}
            onChange={(e) => set("description", e.target.value)}
          />
        </div>

        {/* Learning objectives */}
        <div className="ac-form-group">
          <label>Learning Objectives</label>
          <textarea
            rows={3}
            value={form.learningObjectives}
            onChange={(e) => set("learningObjectives", e.target.value)}
          />
        </div>

        {/* Lessons / Days / Duration */}
        <div className="ac-form-row">
          <div className="ac-form-group">
            <label>Total Lessons</label>
            <input
              type="number" min={1}
              value={form.totalLessons}
              onChange={(e) => set("totalLessons", e.target.value)}
            />
          </div>
          <div className="ac-form-group">
            <label>Total Days</label>
            <input
              type="number" min={1}
              value={form.totalDays}
              onChange={(e) => set("totalDays", e.target.value)}
            />
          </div>
        </div>

        <div className="ac-form-row">
          <div className="ac-form-group">
            <label>Default Session Duration (mins)</label>
            <input
              type="number" min={1}
              value={form.sessionDurationMins}
              onChange={(e) => set("sessionDurationMins", e.target.value)}
            />
          </div>
          <div className="ac-form-group">
            <label>Thumbnail URL</label>
            <input
              value={form.thumbnailUrl}
              onChange={(e) => set("thumbnailUrl", e.target.value)}
              placeholder="https://..."
            />
          </div>
        </div>

        {/* Batch mode */}
        <div className="ac-toggle-row">
          <label className="ac-switch">
            <input
              type="checkbox"
              checked={form.batchModeEnabled}
              onChange={(e) => set("batchModeEnabled", e.target.checked)}
            />
            <span className="ac-switch-slider" />
          </label>
          <label>Batch Mode</label>
        </div>

        {form.batchModeEnabled && (
          <div className="ac-form-row">
            <div className="ac-form-group">
              <label>Batch Price (₹)</label>
              <input
                type="number" min={0} step="0.01"
                value={form.priceBatchRupees}
                onChange={(e) => set("priceBatchRupees", e.target.value)}
                placeholder="2999"
              />
            </div>
            <div className="ac-form-group">
              <label>Min / Max Batch Size</label>
              <div style={{ display: "flex", gap: "0.5rem" }}>
                <input
                  type="number" min={1}
                  value={form.minBatchSize}
                  onChange={(e) => set("minBatchSize", e.target.value)}
                  placeholder="Min"
                />
                <input
                  type="number" min={1}
                  value={form.maxBatchSize}
                  onChange={(e) => set("maxBatchSize", e.target.value)}
                  placeholder="Max"
                />
              </div>
            </div>
          </div>
        )}

        {/* Personal mode */}
        <div className="ac-toggle-row">
          <label className="ac-switch">
            <input
              type="checkbox"
              checked={form.personalModeEnabled}
              onChange={(e) => set("personalModeEnabled", e.target.checked)}
            />
            <span className="ac-switch-slider" />
          </label>
          <label>Personal Mode</label>
        </div>

        {form.personalModeEnabled && (
          <div className="ac-form-group" style={{ maxWidth: "200px" }}>
            <label>Personal Price (₹)</label>
            <input
              type="number" min={0} step="0.01"
              value={form.pricePersonalRupees}
              onChange={(e) => set("pricePersonalRupees", e.target.value)}
              placeholder="5999"
            />
          </div>
        )}

        <div className="ac-form-actions">
          <button type="submit" className="ac-btn ac-btn-success" disabled={saving}>
            {saving ? "Saving…" : isEdit ? "Update Course" : "Create Course"}
          </button>
          <button
            type="button"
            className="ac-btn ac-btn-ghost"
            onClick={() => navigate("/admin/courses")}
          >
            Cancel
          </button>
        </div>
      </form>
    </div>
  );
};

export default AdminCourseFormPage;
